import posthog, { type CaptureResult } from "posthog-js";

// PostHog rage and dead clicks, on public marketing pages only. PostHog starts
// with both off (providers.tsx); PostHogClickCapture in the (site) layout turns
// them on for marketing routes and off again on any other route or when the
// layout unmounts. The upload layout never mounts it. Off means no click work
// at all, so product pages spend no rate-limit tokens and no session activity
// on clicks (failure cases: tests/posthog/rage-dead-clicks-failure-cases.md).

// Autocapture is on only because posthog-js emits $rageclick from its click
// listener; beforeSendToPostHog drops the $autocapture events it also makes.
const RAGE_CLICK_AUTOCAPTURE = { dom_event_allowlist: ["click" as const], capture_copied_text: false };

// posthog-js/dist/dead-clicks-autocapture (bundled by PostHogClickCapture, so
// no script is ever loaded) registers this factory. stop() disconnects the
// MutationObserver and removes its listeners but leaves a pending check timer.
interface DeadClickDetector {
  start(target: Node): void;
  stop(): void;
}
interface PostHogExtensions {
  initDeadClicksAutocapture?: (instance: typeof posthog, config: object) => DeadClickDetector;
}

let isMarketingRoute = false;
let stopDeadClicks: (() => void) | null = null;
// Captures a stopped detector attempted (its pending check timer); for the harness.
let blockedDeadClickCaptures = 0;

function startDeadClicks(createDetector: NonNullable<PostHogExtensions["initDeadClicksAutocapture"]>): () => void {
  let isRunning = true;
  // The detector captures through this stand-in for posthog, so a click still
  // waiting in its check timer when it stops is never captured.
  const instance = new Proxy(posthog, {
    get(target, property) {
      if (property === "capture") {
        return (...args: unknown[]) => {
          if (isRunning) return Reflect.apply(target.capture, target, args);
          blockedDeadClickCaptures++;
          return undefined;
        };
      }
      return Reflect.get(target, property);
    },
  });
  const detector = createDetector(instance, {});
  detector.start(document);
  return () => {
    isRunning = false;
    detector.stop();
  };
}

/** Applies the current route's setting once PostHog has loaded; providers.tsx calls it from loaded(). */
export function applyClickCapture(): void {
  if (!posthog.__loaded) return;
  // set_config starts autocapture's listeners once /flags is known. Turning it
  // off leaves them attached, but Autocapture._captureEvent returns before any
  // rage check or capture() while config.autocapture is false.
  if (Boolean(posthog.config.autocapture) !== isMarketingRoute) {
    posthog.set_config({ autocapture: isMarketingRoute ? RAGE_CLICK_AUTOCAPTURE : false });
  }
  if (isMarketingRoute && !stopDeadClicks) {
    const extensions = (window as unknown as { __PosthogExtensions__?: PostHogExtensions }).__PosthogExtensions__;
    if (extensions?.initDeadClicksAutocapture) stopDeadClicks = startDeadClicks(extensions.initDeadClicksAutocapture);
  } else if (!isMarketingRoute && stopDeadClicks) {
    stopDeadClicks();
    stopDeadClicks = null;
  }
}

export function setMarketingRoute(isMarketing: boolean): void {
  isMarketingRoute = isMarketing;
  try {
    applyClickCapture();
  } catch {
    // Analytics failures must not interrupt navigation.
  }
}

/** Rage or dead clicks that would be sent while capture is off (a safety net for before_send). */
export function isClickEventOutsideMarketing(event: CaptureResult | null): boolean {
  return !isMarketingRoute && (event?.event === "$rageclick" || event?.event === "$dead_click");
}

/** For the browser harness: what is running now. */
export function getClickCaptureState(): { autocapture: boolean; deadClicks: boolean; blockedDeadClickCaptures: number } {
  return {
    autocapture: Boolean(posthog.config?.autocapture),
    deadClicks: stopDeadClicks !== null,
    blockedDeadClickCaptures,
  };
}
